import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Paper,
    Typography,
    Chip,
    Button,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    CircularProgress,
    Tooltip,
    Alert,
    useTheme,
    alpha,
} from '@mui/material';
import type { Theme } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { getAllFp1Devices } from '../fp1.api';
import { Fp1Device } from '../fp1.types';

// ── Business constants ─────────────────────────────────────────────────────
// Monthly value per pod, supplied by the business (not stored per-record).
const ORG_RATE = 60.0; // org subscription, per pod / month
const ATHLETE_RATE = 69.99; // athlete subscription, per pod / month

type HolderType = 'org' | 'athlete';

interface HolderAgg {
    name: string;
    type: HolderType;
    pods: number;
    rate: number;
    /** Earliest subscription start across this holder's pods (YYYY-MM-DD). */
    startDate: string | null;
    /** Most months behind across this holder's pods (0 when current). */
    monthsPastDue: number;
}

// ── Formatters ─────────────────────────────────────────────────────────────
const fmt = (n: number) => n.toLocaleString('en-US');
// Rounded, thousands-separated dollars (e.g. $22,550) — matches the design mock.
const usd = (n: number) => `$${Math.round(n).toLocaleString('en-US')}`;

const startLabel = (d: string | null) =>
    d
        ? new Date(d).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
          })
        : '—';

const monthsBetween = (start: Date, end: Date) => {
    let months =
        (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
    if (end.getDate() < start.getDate()) months -= 1;
    return Math.max(0, months);
};

const heldForLabel = (d: string | null) => {
    if (!d) return '—';
    const months = monthsBetween(new Date(d), new Date());
    if (months < 1) return '< 1 mo';
    const y = Math.floor(months / 12);
    const m = months % 12;
    if (y && m) return `${y} yr ${m} mo`;
    if (y) return `${y} yr`;
    return `${m} mo`;
};

// ── Per-list helpers ───────────────────────────────────────────────────────
const podsOf = (list: HolderAgg[]) => list.reduce((s, h) => s + h.pods, 0);
const mrrOf = (list: HolderAgg[]) => list.reduce((s, h) => s + h.pods * h.rate, 0);

const sortByPods = (m: Map<string, HolderAgg>) =>
    Array.from(m.values()).sort((a, b) => b.pods - a.pods || a.name.localeCompare(b.name));

// ── Status colour mapping (spec → MUI theme) ───────────────────────────────
const statusColor = (theme: Theme, key: string): string => {
    switch (key) {
        case 'subscribed':
            return theme.palette.success.main;
        case 'comp':
            return theme.palette.primary.main;
        case 'invoice':
            return theme.palette.secondary.main;
        case 'pastDue':
            return theme.palette.warning.main;
        case 'noPayment':
            return theme.palette.error.dark;
        case 'inStock':
            return theme.palette.info.main;
        case 'other':
        default:
            return theme.palette.grey[600];
    }
};

interface InventoryRow {
    name: string;
    count: number;
    description: string;
}

// Revenue holder (org vs athlete) for a deployed pod. Prefers the backend's
// authoritative flag; falls back to token / linked-subscription / org. Shared by
// the summary aggregation and the CSV export so they classify identically.
const resolveHolder = (d: Fp1Device): HolderType => {
    const sub = (d.subscription_status || '').toLowerCase();
    if (d.subscription_holder === 'athlete' || d.subscription_holder === 'org') {
        return d.subscription_holder;
    }
    if (sub === 'active_athlete') return 'athlete';
    if (sub === 'active_org' || sub === 'partial_org') return 'org';
    if (d.stripe_linked_subscription?.subscription_id) return 'athlete';
    if (d.organization_uuid && (d.organization_name || d.assigned_org?.name)) return 'org';
    return 'athlete';
};

const holderName = (d: Fp1Device, holder: HolderType): string =>
    holder === 'org'
        ? d.organization_name || d.assigned_org?.name || 'Unknown organization'
        : d.athlete_name || d.assigned_athlete?.name || d.assigned_athlete?.email || 'Unassigned';

// Top-level category + nested row a pod lands in on the Management page. Mirrors
// the aggregation logic; used by the CSV export so each row is labelled. NOTE:
// partial_org pods are split paid/unpaid by org seats in the aggregate, which can't
// be decided per-pod, so they're labelled "Partial Org" with org seat columns.
const classifyPod = (d: Fp1Device): { category: string; row: string } => {
    const status = d.status;
    if (status === 'unknown') return { category: 'Other', row: 'Unknown custody' };
    if (status === 'decommissioned') return { category: 'Other', row: 'Decommissioned' };
    if (status === 'defective') return { category: 'Other', row: 'Defective' };
    if (status === 'maintenance') return { category: 'In stock', row: 'Maintenance' };
    if (status === 'in_stock') return { category: 'In stock', row: 'In warehouse' };
    if (status !== 'deployed') return { category: 'Excluded', row: status };

    const holder = resolveHolder(d);
    const sub = (d.subscription_status || '').toLowerCase();
    if (sub === 'comp') return { category: 'Comp Pods', row: 'Comp (no charge)' };
    if (sub === 'invoiced') return { category: 'Invoice Pods', row: 'Invoiced (external)' };
    if (sub === 'past_due') {
        return {
            category: 'Past due',
            row: `${holder === 'org' ? 'Org' : 'Athlete'} Past Due`,
        };
    }
    if (sub === 'partial_org') {
        return { category: 'Subscribed / No payment', row: 'Partial Org (split by seats)' };
    }
    if (sub === 'active' || sub === 'active_org' || sub === 'active_athlete') {
        return { category: 'Subscribed', row: holder === 'org' ? 'Active Org' : 'Active Athlete' };
    }
    return { category: 'No payment', row: 'No subscription' };
};

// Module-level cache of the last loaded fleet. Survives tab unmount/remount so
// re-opening the Management subtab paints instantly, then refreshes in the
// background. Lives for the session (cleared on full page reload).
let fleetCache: Fp1Device[] | null = null;

const Fp1ManagementTab: React.FC = () => {
    const theme = useTheme();
    const [devices, setDevices] = useState<Fp1Device[]>(() => fleetCache ?? []);
    // Full-screen spinner only on the very first load (no cache yet).
    const [loading, setLoading] = useState(fleetCache === null);
    // Subtle indicator when we have cached data and are re-fetching behind it.
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        const hasCache = fleetCache !== null;
        if (hasCache) setRefreshing(true);
        else setLoading(true);
        setError(null);
        getAllFp1Devices()
            .then((all) => {
                fleetCache = all;
                if (!cancelled) setDevices(all);
            })
            .catch((err) => {
                console.error('Failed to load pod fleet for Management summary', err);
                // Only block the screen with an error if we have nothing cached to show.
                if (!cancelled && !hasCache) {
                    setError('Failed to load pod fleet. Please try again.');
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setLoading(false);
                    setRefreshing(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const summary = useMemo(() => {
        const mk = () => new Map<string, HolderAgg>();
        const subOrg = mk();
        const subAth = mk();
        const partialOrg = mk(); // org has an active sub but fewer paid seats than deployed pods
        const partialSeats = new Map<string, number>(); // paid org-pod seats, keyed like the partialOrg map
        const invoiced = mk(); // paying via a manual external invoice (not Stripe)
        const comp = mk();
        const pastDueOrg = mk();
        const pastDueAth = mk();
        const noSub = mk(); // No payment category — deployed with no active subscription

        // Inventory / non-revenue tallies.
        let inWarehouse = 0; // In stock category
        let maintenance = 0; // In stock category
        let decommissioned = 0; // Decommissioned category
        let defective = 0; // Decommissioned category
        let unknownCustody = 0; // Unknown category — custody_status = 'unknown'

        const add = (
            map: Map<string, HolderAgg>,
            type: HolderType,
            name: string,
            rate: number,
            device: Fp1Device,
        ) => {
            const key = `${type}:${name}`;
            const ex =
                map.get(key) ??
                ({ name, type, pods: 0, rate, startDate: null, monthsPastDue: 0 } as HolderAgg);
            ex.pods += 1;
            const start =
                device.subscription_started_at ||
                device.deployed_at ||
                device.last_shipped_at ||
                null;
            if (start && (!ex.startDate || start < ex.startDate)) ex.startDate = start;
            const mpd = device.months_past_due ?? 0;
            if (mpd > ex.monthsPastDue) ex.monthsPastDue = mpd;
            map.set(key, ex);
        };

        for (const d of devices) {
            // "Unknown" is an FP1 custody status (stored in the side-channel fp1_status and
            // surfaced as `status`, like maintenance/defective). Driven by the Devices tab;
            // such pods carry no org / athlete / subscription.
            const status = d.status;
            if (status === 'unknown') {
                unknownCustody += 1;
                continue;
            }
            if (status === 'decommissioned') {
                decommissioned += 1;
                continue;
            }
            if (status === 'defective') {
                defective += 1; // shown under the Decommissioned category
                continue;
            }
            if (status === 'maintenance') {
                maintenance += 1; // In stock category
                continue;
            }
            if (status === 'in_stock') {
                inWarehouse += 1; // In stock category
                continue;
            }
            // 'returned' and any other non-deployed status are intentionally not counted.
            if (status !== 'deployed') continue;

            const sub = (d.subscription_status || '').toLowerCase();
            const holder = resolveHolder(d);
            const name = holderName(d, holder);
            const rate = holder === 'org' ? ORG_RATE : ATHLETE_RATE;

            if (sub === 'comp') {
                add(comp, holder, name, 0, d); // kept in Subscribed at $0
            } else if (sub === 'invoiced') {
                // Paying via a manual external invoice — its own Subscribed row (amount unknown).
                add(invoiced, holder, name, 0, d);
            } else if (sub === 'past_due') {
                add(holder === 'org' ? pastDueOrg : pastDueAth, holder, name, rate, d);
            } else if (sub === 'partial_org') {
                // Org has an active subscription but fewer paid seats than deployed pods.
                // Track every partial-org pod here; below we split each org into paid
                // (= its billed seats) vs unpaid (the overflow → No payment category).
                add(partialOrg, holder, name, rate, d);
                partialSeats.set(`org:${name}`, d.org_subscription_quantity ?? 0);
            } else if (sub === 'active' || sub === 'active_athlete' || sub === 'active_org') {
                add(holder === 'org' ? subOrg : subAth, holder, name, rate, d);
            } else {
                // Deployed with no live subscription (no_sub) → No payment category.
                add(noSub, holder, name, rate, d);
            }
        }

        const subOrgL = sortByPods(subOrg);
        const subAthL = sortByPods(subAth);
        const invoicedL = sortByPods(invoiced);
        const compL = sortByPods(comp);
        const pastDueOrgL = sortByPods(pastDueOrg);
        const pastDueAthL = sortByPods(pastDueAth);

        const noSubL = sortByPods(noSub);

        // Partial orgs: only their billed seats count as subscribed; the overflow
        // (deployed pods beyond paid seats) is unpaid and moves to the No payment category.
        const partialOrgL: HolderAgg[] = [];
        const partialUnpaidL: HolderAgg[] = [];
        for (const o of sortByPods(partialOrg)) {
            const seats = Math.max(0, partialSeats.get(`org:${o.name}`) ?? 0);
            const paid = Math.min(o.pods, seats);
            const unpaid = o.pods - paid;
            if (paid > 0) partialOrgL.push({ ...o, pods: paid });
            if (unpaid > 0) partialUnpaidL.push({ ...o, pods: unpaid });
        }

        const subPods = podsOf(subOrgL) + podsOf(subAthL) + podsOf(partialOrgL);
        const compPods = podsOf(compL);
        const invoicePods = podsOf(invoicedL);
        const pastPods = podsOf(pastDueOrgL) + podsOf(pastDueAthL);
        const noPaymentPods = podsOf(partialUnpaidL) + podsOf(noSubL);
        const stockPods = inWarehouse + maintenance;
        // "Other" = decommissioned + defective + unknown-custody.
        const otherPods = decommissioned + defective + unknownCustody;
        const TOTAL =
            subPods + compPods + invoicePods + pastPods + noPaymentPods + stockPods + otherPods;

        // Partial-org pods still sit on an active org subscription, so they count toward
        // monthly revenue; invoiced/comp do not (external / no charge).
        const activeMRR = mrrOf(subOrgL) + mrrOf(subAthL) + mrrOf(partialOrgL);
        const deployedPct = TOTAL ? Math.round((subPods / TOTAL) * 100) : 0;

        const inStockBreakdown: InventoryRow[] = [
            { name: 'In warehouse', count: inWarehouse, description: 'Ready to ship / deploy' },
            { name: 'Maintenance', count: maintenance, description: 'Under repair / servicing' },
        ].filter((r) => r.count > 0);

        const otherBreakdown: InventoryRow[] = [
            {
                name: 'Decommissioned',
                count: decommissioned,
                description: 'Retired / removed from service',
            },
            { name: 'Defective', count: defective, description: 'Flagged defective' },
            {
                name: 'Unknown custody',
                count: unknownCustody,
                description: "Location unknown / haven't seen in a while",
            },
        ].filter((r) => r.count > 0);

        return {
            subOrgL,
            subAthL,
            partialOrgL,
            partialUnpaidL,
            noSubL,
            invoicedL,
            compL,
            pastDueOrgL,
            pastDueAthL,
            inStockBreakdown,
            otherBreakdown,
            subPods,
            compPods,
            invoicePods,
            pastPods,
            noPaymentPods,
            stockPods,
            otherPods,
            TOTAL,
            activeMRR,
            deployedPct,
        };
    }, [devices]);

    if (loading) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    py: 8,
                    gap: 2,
                }}
            >
                <CircularProgress />
                <Typography color="text.secondary">Loading pod fleet…</Typography>
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ py: 4 }}>
                <Alert severity="error">{error}</Alert>
            </Box>
        );
    }

    const asOf = new Date().toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
    });

    // Per-pod CSV of the whole fleet with the Management category/row each pod lands in,
    // so it can be compared side-by-side with the Devices CSV.
    const downloadCsv = () => {
        const headers = [
            'serial_number',
            'fp1_status',
            'subscription_status',
            'subscription_holder',
            'management_category',
            'management_row',
            'organization_name',
            'athlete_name',
            'org_pod_count',
            'org_subscription_quantity',
            'months_past_due',
            'subscription_started_at',
        ];
        const esc = (v: unknown) => {
            const s = v === null || v === undefined ? '' : String(v);
            return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
        };
        const rows = devices.map((d) => {
            const { category, row } = classifyPod(d);
            const holder = d.status === 'deployed' ? resolveHolder(d) : '';
            return [
                d.serial_number,
                d.status,
                d.subscription_status ?? '',
                d.subscription_holder ?? holder,
                category,
                row,
                d.organization_name ?? d.assigned_org?.name ?? '',
                d.athlete_name ?? d.assigned_athlete?.name ?? d.assigned_athlete?.email ?? '',
                d.org_pod_count ?? '',
                d.org_subscription_quantity ?? '',
                d.months_past_due ?? '',
                d.subscription_started_at ?? '',
            ]
                .map(esc)
                .join(',');
        });
        const csv = [headers.join(','), ...rows].join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `fp1-management-${asOf.replace(/[ ,]+/g, '-')}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const proportionSegments = [
        { key: 'subscribed', label: 'Subscribed', count: summary.subPods },
        { key: 'comp', label: 'Comp', count: summary.compPods },
        { key: 'invoice', label: 'Invoice', count: summary.invoicePods },
        { key: 'pastDue', label: 'Past due', count: summary.pastPods },
        { key: 'noPayment', label: 'No payment', count: summary.noPaymentPods },
        { key: 'inStock', label: 'In stock', count: summary.stockPods },
        { key: 'other', label: 'Other', count: summary.otherPods },
    ].filter((s) => s.count > 0);

    // ── Reusable renderers ─────────────────────────────────────────────────
    const Metric: React.FC<{ value: string; label: string }> = ({ value, label }) => (
        <Box sx={{ textAlign: 'right', minWidth: 120 }}>
            <Typography sx={{ fontWeight: 800, fontSize: '1.35rem', lineHeight: 1.1 }}>
                {value}
            </Typography>
            <Typography variant="caption" color="text.secondary">
                {label}
            </Typography>
        </Box>
    );

    const subscribedTable = (list: HolderAgg[], showValue: boolean) => (
        <Table size="small">
            <TableHead>
                <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Holder</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                        Pods
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Started</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Held for</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Term</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                        Monthly value
                    </TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {list.map((h) => (
                    <TableRow key={`${h.type}:${h.name}`} hover>
                        <TableCell>{h.name}</TableCell>
                        <TableCell align="right">{fmt(h.pods)}</TableCell>
                        <TableCell>{startLabel(h.startDate)}</TableCell>
                        <TableCell>{heldForLabel(h.startDate)}</TableCell>
                        <TableCell>{showValue ? 'Monthly' : '—'}</TableCell>
                        <TableCell align="right">
                            {showValue ? `${usd(h.pods * h.rate)}/mo` : '$0'}
                        </TableCell>
                    </TableRow>
                ))}
                <TableRow>
                    <TableCell sx={{ fontWeight: 800 }}>Total</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800 }}>
                        {fmt(podsOf(list))}
                    </TableCell>
                    <TableCell colSpan={3} />
                    <TableCell align="right" sx={{ fontWeight: 800 }}>
                        {showValue ? `${usd(mrrOf(list))}/mo` : '$0'}
                    </TableCell>
                </TableRow>
            </TableBody>
        </Table>
    );

    // Holder | Pods | Potential value/mo — for the No payment breakdown (revenue at risk).
    const noPaymentTable = (list: HolderAgg[]) => (
        <Table size="small">
            <TableHead>
                <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Holder</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                        Pods
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                        Potential value
                    </TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {list.map((h) => (
                    <TableRow key={`${h.type}:${h.name}`} hover>
                        <TableCell>{h.name}</TableCell>
                        <TableCell align="right">{fmt(h.pods)}</TableCell>
                        <TableCell align="right">{`${usd(h.pods * h.rate)}/mo`}</TableCell>
                    </TableRow>
                ))}
                <TableRow>
                    <TableCell sx={{ fontWeight: 800 }}>Total</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800 }}>
                        {fmt(podsOf(list))}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800 }}>
                        {`${usd(mrrOf(list))}/mo`}
                    </TableCell>
                </TableRow>
            </TableBody>
        </Table>
    );

    const NestedAccordion: React.FC<{
        title: string;
        count: number;
        countNoun: string;
        pods: number;
        money?: string;
        alwaysShow?: boolean;
        children: React.ReactNode;
    }> = ({ title, count, countNoun, pods, money, alwaysShow, children }) => {
        if (count === 0 && !alwaysShow) return null;
        return (
            <Accordion disableGutters elevation={0} sx={{ '&:before': { display: 'none' } }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            width: '100%',
                            flexWrap: 'wrap',
                        }}
                    >
                        <Typography sx={{ fontWeight: 700 }}>{title}</Typography>
                        <Chip
                            size="small"
                            label={`${count} ${countNoun}`}
                            sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                        <Box sx={{ flexGrow: 1 }} />
                        <Typography variant="body2" color="text.secondary">
                            {fmt(pods)} pods
                        </Typography>
                        {money && (
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                {money}
                            </Typography>
                        )}
                    </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>{children}</AccordionDetails>
            </Accordion>
        );
    };

    const StatusPanel: React.FC<{
        index: number;
        colorKey: string;
        title: string;
        description: string;
        pods: number;
        money?: { value: string; label: string };
        children: React.ReactNode;
    }> = ({ index, colorKey, title, description, pods, money, children }) => {
        const color = statusColor(theme, colorKey);
        const pct = summary.TOTAL ? Math.round((pods / summary.TOTAL) * 100) : 0;
        return (
            <Accordion
                defaultExpanded
                disableGutters
                sx={{
                    mb: 2,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    overflow: 'hidden',
                    '&:before': { display: 'none' },
                }}
            >
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                        borderLeft: `4px solid ${color}`,
                        bgcolor: alpha(color, 0.04),
                        '& .MuiAccordionSummary-content': { my: 1.25 },
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 3,
                            width: '100%',
                            flexWrap: 'wrap',
                        }}
                    >
                        {/* Grows to fill the row and lets a long description wrap *inside*
                            this column (minWidth:0), so the revenue + pods blocks always
                            stay pinned to the right — identical layout for every category
                            regardless of description length. */}
                        <Box sx={{ flex: '1 1 260px', minWidth: 0 }}>
                            <Typography sx={{ fontWeight: 800, color }}>
                                {index} · {title}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {description}
                            </Typography>
                        </Box>
                        {money && (
                            <Box sx={{ textAlign: 'right' }}>
                                <Typography sx={{ fontWeight: 800 }}>{money.value}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {money.label}
                                </Typography>
                            </Box>
                        )}
                        <Box sx={{ textAlign: 'right', minWidth: 96, flexShrink: 0 }}>
                            <Typography
                                component="span"
                                sx={{ fontWeight: 800, fontSize: '1.2rem' }}
                            >
                                {fmt(pods)}
                            </Typography>{' '}
                            <Typography component="span" variant="caption" color="text.secondary">
                                pods
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                                {pct}% of fleet
                            </Typography>
                        </Box>
                    </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>{children}</AccordionDetails>
            </Accordion>
        );
    };

    const inventoryCards = (rows: InventoryRow[]) => (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
                gap: 1.5,
            }}
        >
            {rows.map((r) => (
                <Paper key={r.name} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                    <Typography variant="h5" sx={{ fontWeight: 800 }}>
                        {fmt(r.count)}
                    </Typography>
                    <Typography sx={{ fontWeight: 700 }}>{r.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                        {r.description}
                    </Typography>
                </Paper>
            ))}
        </Box>
    );

    const subscribedMoney = usd(summary.activeMRR);
    const pastDuePods = summary.pastPods;

    return (
        <Box sx={{ maxWidth: 980, mx: 'auto', px: { xs: 0.5, sm: 1 }, py: 1 }}>
            {/* Title */}
            <Box
                sx={{
                    mb: 2.5,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    gap: 2,
                    flexWrap: 'wrap',
                }}
            >
                <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="h4" sx={{ fontWeight: 800 }}>
                            FiyrPod — Pod Management
                        </Typography>
                        {refreshing && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                <CircularProgress size={14} thickness={5} />
                                <Typography variant="caption" color="text.secondary">
                                    Refreshing…
                                </Typography>
                            </Box>
                        )}
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Every pod we own, by status. Click any row to drill down. Updated {asOf}.
                    </Typography>
                </Box>
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<FileDownloadIcon />}
                    onClick={downloadCsv}
                    disabled={devices.length === 0}
                >
                    Download CSV
                </Button>
            </Box>

            {summary.TOTAL === 0 ? (
                <Alert severity="info">No pods found.</Alert>
            ) : (
                <>
                    {/* Hero card */}
                    <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, mb: 2.5 }}>
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'flex-end',
                                gap: 3,
                                flexWrap: 'wrap',
                            }}
                        >
                            <Box>
                                <Typography sx={{ fontSize: 52, fontWeight: 800, lineHeight: 1 }}>
                                    {fmt(summary.TOTAL)}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                    total pods purchased / in existence
                                </Typography>
                            </Box>
                            <Box
                                sx={{
                                    display: 'flex',
                                    gap: { xs: 3, sm: 5 },
                                    flexWrap: 'wrap',
                                    justifyContent: 'flex-end',
                                }}
                            >
                                <Metric
                                    value={`${subscribedMoney}/mo`}
                                    label="active monthly revenue"
                                />
                                <Metric
                                    value={`${summary.deployedPct}%`}
                                    label="deployed (subscribed)"
                                />
                            </Box>
                        </Box>

                        {/* Proportion bar */}
                        <Box
                            sx={{
                                display: 'flex',
                                height: 12,
                                borderRadius: 6,
                                overflow: 'hidden',
                                mt: 3,
                                bgcolor: 'action.hover',
                            }}
                        >
                            {proportionSegments.map((s) => {
                                const pct = (s.count / summary.TOTAL) * 100;
                                return (
                                    <Tooltip
                                        key={s.key}
                                        title={`${s.label}: ${fmt(s.count)} (${Math.round(pct)}%)`}
                                    >
                                        <Box
                                            sx={{
                                                width: `${pct}%`,
                                                bgcolor: statusColor(theme, s.key),
                                            }}
                                        />
                                    </Tooltip>
                                );
                            })}
                        </Box>

                        {/* Legend — each marker sits under its bar segment. For narrow
                            segments the label can't fit, so we drop it and reveal the
                            name + count on hover instead. */}
                        <Box sx={{ display: 'flex', width: '100%', mt: 1.25 }}>
                            {proportionSegments.map((s) => {
                                // Rough check: does the label fit within this segment's width?
                                // (bar ≈ 900px wide; ~6.5px per caption character.)
                                const segPx = (s.count / summary.TOTAL) * 900;
                                const labelFits = s.label.length * 6.5 + 12 <= segPx;
                                return (
                                    <Tooltip key={s.key} title={`${s.label} · ${fmt(s.count)}`}>
                                        <Box
                                            sx={{
                                                flexGrow: s.count,
                                                flexBasis: 0,
                                                minWidth: 0,
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                textAlign: 'center',
                                                gap: 0.25,
                                                px: 0.25,
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    width: 10,
                                                    height: 10,
                                                    borderRadius: '50%',
                                                    bgcolor: statusColor(theme, s.key),
                                                    flexShrink: 0,
                                                }}
                                            />
                                            {labelFits && (
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    noWrap
                                                    sx={{ lineHeight: 1.2, maxWidth: '100%' }}
                                                >
                                                    {s.label}
                                                </Typography>
                                            )}
                                            <Typography
                                                variant="caption"
                                                color="text.primary"
                                                sx={{ fontWeight: 700, lineHeight: 1 }}
                                            >
                                                {fmt(s.count)}
                                            </Typography>
                                        </Box>
                                    </Tooltip>
                                );
                            })}
                        </Box>
                    </Paper>

                    {/* Subscribed */}
                    <StatusPanel
                        index={1}
                        colorKey="subscribed"
                        title="Subscribed"
                        description="Active, paying pods — split by organization vs. individual athlete."
                        pods={summary.subPods}
                        money={{ value: `${subscribedMoney}/mo`, label: 'active monthly revenue' }}
                    >
                        <NestedAccordion
                            title="Active Org Subscriptions"
                            count={summary.subOrgL.length}
                            countNoun="subscriptions"
                            pods={podsOf(summary.subOrgL)}
                            money={`${usd(mrrOf(summary.subOrgL))}/mo`}
                            alwaysShow
                        >
                            {subscribedTable(summary.subOrgL, true)}
                        </NestedAccordion>
                        <NestedAccordion
                            title="Active Athlete Subscriptions"
                            count={summary.subAthL.length}
                            countNoun="subscriptions"
                            pods={podsOf(summary.subAthL)}
                            money={`${usd(mrrOf(summary.subAthL))}/mo`}
                            alwaysShow
                        >
                            {subscribedTable(summary.subAthL, true)}
                        </NestedAccordion>
                        <NestedAccordion
                            title="Partial Org (paid seats)"
                            count={summary.partialOrgL.length}
                            countNoun="orgs"
                            pods={podsOf(summary.partialOrgL)}
                            money={`${usd(mrrOf(summary.partialOrgL))}/mo`}
                        >
                            {subscribedTable(summary.partialOrgL, true)}
                        </NestedAccordion>
                    </StatusPanel>

                    {/* Comp / Invoice Pods */}
                    <StatusPanel
                        index={2}
                        colorKey="comp"
                        title="Comp Pods"
                        description="Complimentary pods — no charge, no revenue."
                        pods={summary.compPods}
                    >
                        <NestedAccordion
                            title="Comp (no charge)"
                            count={summary.compL.length}
                            countNoun="holders"
                            pods={podsOf(summary.compL)}
                            alwaysShow
                        >
                            {subscribedTable(summary.compL, false)}
                        </NestedAccordion>
                    </StatusPanel>

                    {/* Invoice Pods */}
                    <StatusPanel
                        index={3}
                        colorKey="invoice"
                        title="Invoice Pods"
                        description="Paid via external invoice — no Stripe MRR (amounts TBD)."
                        pods={summary.invoicePods}
                    >
                        <NestedAccordion
                            title="Invoiced (external)"
                            count={summary.invoicedL.length}
                            countNoun="holders"
                            pods={podsOf(summary.invoicedL)}
                            alwaysShow
                        >
                            {subscribedTable(summary.invoicedL, false)}
                        </NestedAccordion>
                    </StatusPanel>

                    {/* Past due */}
                    <StatusPanel
                        index={4}
                        colorKey="pastDue"
                        title="Past due"
                        description="Subscriptions behind on payment — monthly revenue at risk."
                        pods={pastDuePods}
                        money={{
                            value: `${usd(
                                mrrOf(summary.pastDueOrgL) + mrrOf(summary.pastDueAthL),
                            )}/mo`,
                            label: 'monthly revenue at risk',
                        }}
                    >
                        <NestedAccordion
                            title="Org Past Due"
                            count={summary.pastDueOrgL.length}
                            countNoun="accounts"
                            pods={podsOf(summary.pastDueOrgL)}
                            money={`${usd(mrrOf(summary.pastDueOrgL))}/mo`}
                            alwaysShow
                        >
                            {noPaymentTable(summary.pastDueOrgL)}
                        </NestedAccordion>
                        <NestedAccordion
                            title="Athlete Past Due"
                            count={summary.pastDueAthL.length}
                            countNoun="accounts"
                            pods={podsOf(summary.pastDueAthL)}
                            money={`${usd(mrrOf(summary.pastDueAthL))}/mo`}
                            alwaysShow
                        >
                            {noPaymentTable(summary.pastDueAthL)}
                        </NestedAccordion>
                    </StatusPanel>

                    {/* No payment */}
                    <StatusPanel
                        index={5}
                        colorKey="noPayment"
                        title="No payment"
                        description="Deployed pods nobody is paying for — partial-org overflow + no-sub."
                        pods={summary.noPaymentPods}
                        money={{
                            value: `${usd(
                                mrrOf(summary.partialUnpaidL) + mrrOf(summary.noSubL),
                            )}/mo`,
                            label: 'potential revenue',
                        }}
                    >
                        <NestedAccordion
                            title="Partial org · unpaid"
                            count={summary.partialUnpaidL.length}
                            countNoun="orgs"
                            pods={podsOf(summary.partialUnpaidL)}
                            money={`${usd(mrrOf(summary.partialUnpaidL))}/mo`}
                            alwaysShow
                        >
                            {noPaymentTable(summary.partialUnpaidL)}
                        </NestedAccordion>
                        <NestedAccordion
                            title="No subscription"
                            count={summary.noSubL.length}
                            countNoun="holders"
                            pods={podsOf(summary.noSubL)}
                            money={`${usd(mrrOf(summary.noSubL))}/mo`}
                            alwaysShow
                        >
                            {noPaymentTable(summary.noSubL)}
                        </NestedAccordion>
                    </StatusPanel>

                    {/* In stock */}
                    <StatusPanel
                        index={6}
                        colorKey="inStock"
                        title="In stock"
                        description="Owned but not deployed — available to ship or assign."
                        pods={summary.stockPods}
                    >
                        {summary.inStockBreakdown.length > 0 ? (
                            inventoryCards(summary.inStockBreakdown)
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                No inventory in stock.
                            </Typography>
                        )}
                    </StatusPanel>

                    {/* Other — decommissioned, defective, and unknown-custody pods */}
                    <StatusPanel
                        index={7}
                        colorKey="other"
                        title="Other"
                        description="Decommissioned, defective, and unknown-location pods."
                        pods={summary.otherPods}
                    >
                        {summary.otherBreakdown.length > 0 ? (
                            inventoryCards(summary.otherBreakdown)
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                No other pods.
                            </Typography>
                        )}
                    </StatusPanel>
                </>
            )}
        </Box>
    );
};

export default Fp1ManagementTab;
